import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { MapContainer, TileLayer, CircleMarker, Popup } from "react-leaflet";
import { LineChart, Line, ResponsiveContainer, Tooltip } from "recharts";
import { TrendingUpIcon, AlertOctagonIcon, RadioIcon, PackageIcon, WarehouseIcon, TruckIcon, WrenchIcon, BarChart3Icon } from "lucide-react";

const STATUS_LABEL = {
  EM_TRANSITO: { color: "#00E5FF", label: "Cheio em trânsito" },
  EM_TRANSITO_VAZIO: { color: "#FF9F0A", label: "Vazio em trânsito" },
  CHEIO: { color: "#FFD60A", label: "Cheio · pátio" },
  NO_CLIENTE: { color: "#34C759", label: "No cliente" },
};

function Sparkline({ values, color }) {
  const data = values.map((v, i) => ({ x: i, y: v }));
  return (
    <ResponsiveContainer width="100%" height={40}>
      <LineChart data={data}>
        <Line type="monotone" dataKey="y" stroke={color} strokeWidth={2} dot={false} />
        <Tooltip content={() => null} />
      </LineChart>
    </ResponsiveContainer>
  );
}

function KpiCard({ icon: Icon, label, value, color, testId, sparkData, onClick }) {
  return (
    <Card
      onClick={onClick}
      className={`bg-[#14151A] border-border border-t-2 rounded-none p-5 relative transition-all ${onClick ? "cursor-pointer hover:bg-[#1C1D24] hover:-translate-y-px" : ""}`}
      style={{ borderTopColor: color }}
      data-testid={testId}
    >
      <div className="flex items-start justify-between mb-2">
        <Icon className="w-5 h-5" style={{ color }} />
        <span className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</span>
      </div>
      <div className="kpi-num text-6xl mt-2" style={{ color }}>{value}</div>
      <div className="h-10 mt-2 -mx-2">
        <Sparkline values={sparkData} color={color} />
      </div>
      {onClick && (
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground mt-2 flex items-center gap-1">
          Ver detalhes <span aria-hidden>→</span>
        </div>
      )}
    </Card>
  );
}

export default function Dashboard() {
  const navigate = useNavigate();
  const [kpis, setKpis] = useState({ total: 0, cheios: 0, em_transito: 0, vazios_prontos: 0, em_manutencao: 0, aguardando_inspecao: 0 });
  const [alertas, setAlertas] = useState([]);
  const [telemetria, setTelemetria] = useState([]);
  const [quebra, setQuebra] = useState([]);
  const [saldos, setSaldos] = useState([]);
  const tickRef = useRef(null);

  const load = async () => {
    const [k, a, t, q, s] = await Promise.all([
      api.get("/dashboard/kpis"),
      api.get("/dashboard/alertas"),
      api.get("/dashboard/telemetria"),
      api.get("/dashboard/quebra"),
      api.get("/saldos-granel").catch(() => ({ data: [] })),
    ]);
    setKpis(k.data);
    setAlertas(a.data);
    setTelemetria(t.data);
    setQuebra(q.data);
    setSaldos(s.data);
  };

  useEffect(() => {
    load();
    const interval = setInterval(async () => {
      try { await api.post("/telemetria/tick"); } catch {}
      load();
    }, 8000);
    tickRef.current = interval;
    return () => clearInterval(interval);
  }, []);

  // mock sparkline series anchored to current KPI value
  const spark = (v) => Array.from({ length: 12 }, (_, i) => Math.max(0, v + Math.round(Math.sin(i*0.7)*Math.max(1, v*0.1))));

  return (
    <div className="p-4 md:p-8 max-w-[1800px] mx-auto">
      <header className="mb-6 flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-[0.3em] text-[#00E5FF] mb-1 flex items-center gap-2">
            <RadioIcon className="w-3.5 h-3.5 animate-pulse" /> Tela 04 · Centro de comando
          </div>
          <h1 className="font-display text-4xl md:text-5xl font-bold leading-none">Dashboard de Gestão</h1>
          <p className="text-sm text-muted-foreground mt-2">Telemetria GPS em tempo real, KPIs operacionais e alertas FEFO.</p>
        </div>
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <div className="w-2 h-2 bg-[#34C759] rounded-full animate-pulse" />
          <span>Live · tick 8s</span>
        </div>
      </header>

      {/* KPI Row */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        <KpiCard testId="kpi-cheios" icon={WarehouseIcon} label="Contentores cheios" value={kpis.cheios} color="#FFD60A" sparkData={spark(kpis.cheios)} onClick={() => navigate("/estoque")} />
        <KpiCard testId="kpi-transito" icon={TruckIcon} label="Em trânsito" value={kpis.em_transito} color="#00E5FF" sparkData={spark(kpis.em_transito)} onClick={() => navigate("/expedicao")} />
        <KpiCard testId="kpi-vazios" icon={PackageIcon} label="Vazios prontos" value={kpis.vazios_prontos} color="#34C759" sparkData={spark(kpis.vazios_prontos)} onClick={() => navigate("/triagem?status=VAZIO_PRONTO")} />
        <KpiCard testId="kpi-inspecao" icon={AlertOctagonIcon} label="Aguardando inspeção" value={kpis.aguardando_inspecao || 0} color="#00E5FF" sparkData={spark(kpis.aguardando_inspecao || 0)} onClick={() => navigate("/triagem")} />
        <KpiCard testId="kpi-manutencao" icon={WrenchIcon} label="Em manutenção" value={kpis.em_manutencao} color="#FF3B30" sparkData={spark(kpis.em_manutencao)} onClick={() => navigate("/manutencao")} />
      </div>

      {/* Saldo de Granel por Produto */}
      {saldos.length > 0 && (
        <Card className="bg-[#14151A] border-border rounded-none p-0 overflow-hidden mb-6">
          <div className="px-6 py-4 border-b border-border flex items-center gap-2">
            <BarChart3Icon className="w-5 h-5 text-[#FFD60A]" />
            <h2 className="font-display text-2xl font-bold">Saldo de Granel · Litros disponíveis por produto</h2>
            <span className="text-xs text-muted-foreground ml-2">(soma dos recebimentos − envasado)</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-0 divide-x divide-y divide-border">
            {saldos.map((s) => (
              <div
                key={s.codigo_produto}
                onClick={() => navigate(`/estoque?produto=${s.codigo_produto}`)}
                className="p-5 cursor-pointer hover:bg-[#1C1D24]"
                data-testid={`saldo-${s.codigo_produto}`}
              >
                <div className="flex items-center justify-between mb-1">
                  <span className="text-[10px] uppercase tracking-widest text-muted-foreground">Cód. {s.codigo_produto}</span>
                  <Badge className="rounded-none border bg-transparent border-border text-[9px]">{s.num_remessas} remessa(s)</Badge>
                </div>
                <div className="font-mono text-xs text-[#00E5FF] mb-2 truncate">{s.funcao_quimica}</div>
                <div className="kpi-num text-4xl text-[#FFD60A]" data-testid={`saldo-litros-${s.codigo_produto}`}>{s.saldo_litros.toLocaleString("pt-BR")}</div>
                <div className="text-xs text-muted-foreground mt-1">litros de saldo</div>
                <div className="mt-2 h-1 bg-[#0A0A0C] overflow-hidden">
                  <div
                    className="h-full bg-[#34C759]"
                    style={{ width: `${s.recebido_litros ? (s.envasado_litros / s.recebido_litros) * 100 : 0}%` }}
                  />
                </div>
                <div className="text-[10px] font-mono text-muted-foreground mt-1">
                  {s.envasado_litros.toLocaleString("pt-BR")}L envasado / {s.recebido_litros.toLocaleString("pt-BR")}L recebido
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Map + Alerts */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 mb-6">
        <Card className="lg:col-span-3 bg-[#14151A] border-border rounded-none p-0 overflow-hidden">
          <div className="px-6 py-4 border-b border-border flex items-center justify-between">
            <div>
              <h2 className="font-display text-2xl font-bold">Telemetria GPS</h2>
              <div className="text-xs text-muted-foreground">Geofencing · {telemetria.length} ativos monitorados</div>
            </div>
            <div className="flex items-center gap-3 text-xs">
              {Object.entries(STATUS_LABEL).map(([k, v]) => (
                <div key={k} className="flex items-center gap-1.5">
                  <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: v.color }} />
                  <span className="text-muted-foreground">{v.label}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="h-[480px] w-full">
            <MapContainer center={[-23.55, -46.63]} zoom={9} style={{ height: "100%", width: "100%" }} data-testid="dashboard-map">
              <TileLayer
                url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
                attribution='&copy; OpenStreetMap &copy; CARTO'
              />
              {telemetria.map((t) => {
                const meta = STATUS_LABEL[t.status] || { color: "#A1A1AA" };
                return (
                  <CircleMarker
                    key={t.no_serie}
                    center={[t.gps_lat, t.gps_lng]}
                    radius={8}
                    pathOptions={{ color: meta.color, fillColor: meta.color, fillOpacity: 0.8, weight: 2 }}
                  >
                    <Popup>
                      <div className="text-xs">
                        <div className="font-mono font-bold" style={{ color: meta.color }}>{t.no_serie}</div>
                        <div className="text-muted-foreground mt-1">GPS: {t.gps_id}</div>
                        <div>{t.nome_produto || "—"}</div>
                        <div className="text-muted-foreground">Lote: {t.no_lote || "—"}</div>
                        <div className="text-muted-foreground">Cliente: {t.cliente || "—"}</div>
                      </div>
                    </Popup>
                  </CircleMarker>
                );
              })}
            </MapContainer>
          </div>
        </Card>

        <Card className="lg:col-span-2 bg-[#14151A] border-border rounded-none p-0 overflow-hidden">
          <div className="px-6 py-4 border-b border-border flex items-center justify-between">
            <div className="flex items-center gap-2">
              <AlertOctagonIcon className="w-5 h-5 text-[#FF3B30]" />
              <h2 className="font-display text-2xl font-bold">Alertas Críticos</h2>
            </div>
            <Badge className="rounded-none border bg-[#FF3B30]/15 text-[#FF3B30] border-[#FF3B30]/40">{alertas.length}</Badge>
          </div>
          <div className="max-h-[480px] overflow-y-auto">
            {alertas.length === 0 && (
              <div className="p-6 text-sm text-muted-foreground">Nenhum vencimento próximo nos próximos 30 dias.</div>
            )}
            {alertas.map((a) => (
              <div key={a.no_serie} className="px-5 py-4 border-b border-border/60 hover:bg-[#1C1D24]" data-testid={`alerta-${a.no_serie}`}>
                <div className="flex items-center justify-between">
                  <div className="font-mono text-[#FFD60A] font-bold">{a.no_serie}</div>
                  <Badge className="rounded-none border bg-transparent border-border text-xs">{a.status.replace(/_/g," ")}</Badge>
                </div>
                <div className="mt-2 space-y-1">
                  {a.alertas.map((al, i) => (
                    <div key={i} className="flex items-center justify-between text-xs">
                      <span className="text-muted-foreground">{al.campo.replace(/_/g, " ")}</span>
                      <span className={`font-mono ${al.vencido ? "text-[#FF3B30]" : "text-[#FF9F0A]"}`}>{al.data} {al.vencido && "· VENCIDO"}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* Quebra operacional */}
      <Card className="bg-[#14151A] border-border rounded-none p-0 overflow-hidden">
        <div className="px-6 py-4 border-b border-border flex items-center gap-2">
          <BarChart3Icon className="w-5 h-5 text-[#00E5FF]" />
          <h2 className="font-display text-2xl font-bold">Auditoria de quebra operacional</h2>
          <span className="text-xs text-muted-foreground ml-2">limite: 0.5%</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#0A0A0C] text-[10px] uppercase tracking-widest text-muted-foreground">
              <tr>
                <th className="text-left px-4 py-3">Remessa</th>
                <th className="text-left px-4 py-3">Produto</th>
                <th className="text-right px-4 py-3">Vol. Teórico</th>
                <th className="text-right px-4 py-3">Vol. Envasado</th>
                <th className="text-right px-4 py-3">Quebra %</th>
                <th className="text-left px-4 py-3">Status</th>
              </tr>
            </thead>
            <tbody>
              {quebra.length === 0 && (
                <tr><td colSpan="6" className="text-center text-muted-foreground py-8">Nenhuma remessa concluída ainda.</td></tr>
              )}
              {quebra.map((q) => (
                <tr key={q.remessa} className="border-t border-border/60" data-testid={`quebra-${q.remessa}`}>
                  <td className="px-4 py-3 font-mono text-[#FFD60A]">{q.remessa}</td>
                  <td className="px-4 py-3">{q.produto}</td>
                  <td className="px-4 py-3 text-right font-mono">{q.volume_calculado}</td>
                  <td className="px-4 py-3 text-right font-mono">{q.volume_envasado}</td>
                  <td className={`px-4 py-3 text-right font-mono ${Math.abs(q.quebra_pct) > 0.5 ? "text-[#FF3B30]" : "text-[#34C759]"}`}>{q.quebra_pct.toFixed(2)}%</td>
                  <td className="px-4 py-3">
                    {Math.abs(q.quebra_pct) > 0.5
                      ? <Badge className="rounded-none border bg-[#FF3B30]/15 text-[#FF3B30] border-[#FF3B30]/40">Auditoria</Badge>
                      : <Badge className="rounded-none border bg-[#34C759]/15 text-[#34C759] border-[#34C759]/40">OK</Badge>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
